import React, { useState } from "react"
import { useNavigate, useParams } from "react-router"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField, Stack, MenuItem } from "@mui/material";
import { togglePreQuoteCreateDialog } from "../../../../app/slices/VerificationModuleSlice";
import { useCreatePreQuoteMutation } from "../../../../services/verifyService/verify-api";
import { PreQuoteCreateCommand } from "../../../../services/verifyService/commands";
import { usePayerListQuery } from "../../../../services/orgService/org-api";
import { Payer } from "../../../../services/orgService/model/payer/Payer";

function PreQuoteCreateDialog(){
    const {center} = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isOpen = useAppSelector((state) => state.verificationModule.PreQuoteCreateDialog);
    const [createPreQuote] = useCreatePreQuoteMutation();
    const { data: payers } = usePayerListQuery(center!);
    const sortedPayers = payers ? [...payers].sort((a, b) => a.name.localeCompare(b.name)) : [];
    const [notify, setNotify] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [patientName, setPatientName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [admissionType, setAdmissionType] = useState('');
    const [serviceDate, setServiceDate] = useState('');
    const [selectedPayer, setSelectedPayer] = useState<Payer | null>(null);
    const [policyNumber, setPolicyNumber] = useState('');
    const [groupNumber, setGroupNumber] = useState('');
    const [cptCodes, setCptCodes] = useState('');
    const [note, setNote] = useState('');

    const isValid = !!patientName.trim() && !!birthDate && !!admissionType && !!serviceDate && !!selectedPayer && !!policyNumber.trim() && !!cptCodes.trim();

    const handleClose = () => {
        resetForm();
        dispatch(togglePreQuoteCreateDialog());
    };

    const resetForm = () => {
        setPatientName('');
        setPhoneNumber('');
        setBirthDate('');
        setAdmissionType('');
        setServiceDate('');
        setSelectedPayer(null);
        setPolicyNumber('');
        setGroupNumber('');
        setCptCodes('');
        setNote('');
    };

    const handleSubmit = async () => {
        if (!isValid) return;
        try {
            setSubmitting(true);
                        // Convert ISO date strings to proper DateTime format
                        const formatDate = (dateString: string | undefined): string | undefined => {
                            if (!dateString) return undefined;
                            // dateString is in format "YYYY-MM-DD" from HTML date input
                            // Convert to ISO 8601 with time: "YYYY-MM-DDTHH:mm:ss.sssZ"
                            return dateString + 'T00:00:00';
                        };

            const command: PreQuoteCreateCommand = {
                center: center!,
                patientName: patientName.trim(),
                phoneNumber: phoneNumber.trim() || undefined,
                   birthDate: formatDate(birthDate),
                admissionType: admissionType || undefined,
                   serviceDate: formatDate(serviceDate),
                primaryPayerCode: selectedPayer?.code || undefined,
                primaryPayerName: selectedPayer?.name || undefined,
                policyNumber: policyNumber.trim() || undefined,
                groupNumber: groupNumber.trim() || undefined,
                cptCodes: cptCodes.trim() ? cptCodes.split(',').map(c => c.trim()).filter(c => c) : undefined,
                note: note.trim() || undefined,
            };
            const result = await createPreQuote(command).unwrap();
            resetForm();
            dispatch(togglePreQuoteCreateDialog());
            navigate('/Verification/' + result.verificationId);
        } catch (error) {
            setNotify(true);
        } finally {
            setSubmitting(false);
        }
    };

    return <React.Fragment>
        <Dialog open={isOpen} maxWidth="sm" fullWidth>
            <DialogTitle sx={{color: '#9C27B0'}}>New Pre Quote</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <TextField
                        label="Patient Name"
                        value={patientName}
                        onChange={(e) => setPatientName(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        placeholder="Last, First"
                    />
                    <TextField
                        label="Phone Number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="(555) 123-4567"
                    />
                    <TextField
                        label="Birth Date"
                        type="date"
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        InputLabelProps={{shrink: true}}
                    />
                    <TextField
                        label="Admission Type"
                        value={admissionType}
                        onChange={(e) => setAdmissionType(e.target.value)}
                        select
                        required
                        fullWidth
                        size="small"
                    >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="OP">Outpatient</MenuItem>
                        <MenuItem value="IP">Inpatient</MenuItem>
                    </TextField>
                    <TextField
                        label="Service Date"
                        type="date"
                        value={serviceDate}
                        onChange={(e) => setServiceDate(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        InputLabelProps={{shrink: true}}
                    />
                    <Autocomplete
                        options={sortedPayers}
                        getOptionLabel={(option) => option.name}
                        value={selectedPayer}
                        onChange={(_, value) => setSelectedPayer(value)}
                        renderInput={(params) => (
                            <TextField {...params} label="Primary Payer" size="small" required />
                        )}
                        fullWidth
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                    />
                    <TextField
                        label="Policy Number"
                        value={policyNumber}
                        onChange={(e) => setPolicyNumber(e.target.value)}
                        required
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Group Number"
                        value={groupNumber}
                        onChange={(e) => setGroupNumber(e.target.value)}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="CPT Codes"
                        value={cptCodes}
                        onChange={(e) => setCptCodes(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        placeholder="27447, 27446"
                        helperText="Comma-separated CPT codes"
                    />
                    <TextField
                        label="Note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        size="small"
                        placeholder="Pre-quote notes or context"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color='secondary'>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!isValid || submitting}
                    sx={{color: '#9C27B0'}}
                >
                    {submitting ? 'Creating...' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
        <Snackbar open={notify} onClose={() => setNotify(false)} autoHideDuration={6000}>
            <Alert severity='error'>Failed to create pre-quote. Please try again.</Alert>
        </Snackbar>
    </React.Fragment>
}

export {PreQuoteCreateDialog}
